/**
 * Remembers which cell each store lives in, and which store is currently active. See "Region and
 * Cell Routing" in AGENTS.md for why a store's cell is independent of the cell it signed in against.
 *
 * Zero imports on purpose: EnvironmentUtil.getRegion reads this state, so importing the Redux store
 * here would close a cycle through toolSlice -> ToolMenuConfigService -> EnvironmentUtil. Hence the
 * active store id being pushed in by storeContextMiddleware, and regions being stored exactly as the
 * gateway reported them with normalising left to RegionRealms.
 *
 * Backed by sessionStorage: a mapping is worth keeping across reloads within a session, but
 * must not outlive it, since a store can be re-homed to another cell between sessions.
 */

const STORE_REGIONS_KEY = 'storeRegions'
const ACTIVE_STORE_KEY = 'activeStoreId'

let storeRegions: Record<string, string> | undefined
let activeStoreId: string | undefined

const loadStoreRegions = (): Record<string, string> => {
    if (storeRegions) {
        return storeRegions
    }
    let loaded: Record<string, string> = {}
    try {
        const parsed = JSON.parse(sessionStorage.getItem(STORE_REGIONS_KEY) ?? 'null')
        if (typeof parsed === 'object' && parsed !== null) {
            loaded = parsed
        }
    } catch {
        // Unparseable or unavailable storage: start clean and keep the map in memory only.
    }
    storeRegions = loaded
    return loaded
}

const persistStoreRegions = (): void => {
    try {
        sessionStorage.setItem(STORE_REGIONS_KEY, JSON.stringify(storeRegions ?? {}))
    } catch {
        // Storage can be unavailable in embedded webviews; the in-memory map still works.
    }
}

/** Records the cell a store lives in, as reported by GetStore. */
export const recordStoreRegion = (storeId: string | undefined, region: string | undefined): void => {
    if (!storeId || !region) {
        return
    }
    const regions = loadStoreRegions()
    if (regions[storeId] === region) {
        return
    }
    regions[storeId] = region
    persistStoreRegions()
}

/** The cell a store lives in, if it has been learned this session. */
export const getStoreRegion = (storeId: string | undefined): string | undefined =>
    storeId ? loadStoreRegions()[storeId] : undefined

/**
 * Drops what we learned about a store, so the next lookup searches again. Used when the cell we
 * recorded stops recognising the store, which is what a re-homed store looks like from here.
 */
export const forgetStoreRegion = (storeId: string | undefined): void => {
    if (!storeId) {
        return
    }
    const regions = loadStoreRegions()
    if (!(storeId in regions)) {
        return
    }
    delete regions[storeId]
    persistStoreRegions()
}

/** Called by storeContextMiddleware whenever the selected or scanned store changes. */
export const setActiveStoreId = (storeId: string | undefined): void => {
    activeStoreId = storeId
    try {
        if (storeId) {
            sessionStorage.setItem(ACTIVE_STORE_KEY, storeId)
        } else {
            sessionStorage.removeItem(ACTIVE_STORE_KEY)
        }
    } catch {
        // See persistStoreRegions.
    }
}

export const getActiveStoreId = (): string | undefined => {
    if (activeStoreId !== undefined) {
        return activeStoreId
    }
    try {
        return sessionStorage.getItem(ACTIVE_STORE_KEY) ?? undefined
    } catch {
        return undefined
    }
}

/** The cell owning the active store, if one is active and its cell is known. */
export const getActiveStoreRegion = (): string | undefined => getStoreRegion(getActiveStoreId())

/** @internal Clears all cached state — for testing only. */
export const _resetStoreRegionRegistry = (): void => {
    storeRegions = undefined
    activeStoreId = undefined
    try {
        sessionStorage.removeItem(STORE_REGIONS_KEY)
        sessionStorage.removeItem(ACTIVE_STORE_KEY)
    } catch {
        // See persistStoreRegions.
    }
}
